/**
 * Shared, marketing-safe company facts + stats — the single source of truth for
 * the numbers shown on the public About page (`about-content.tsx`) AND ingested
 * into the Sunny visitor-chat knowledge index (`company-knowledge.ts`). Keeping
 * them here prevents stat drift between the public site and what Sunny cites.
 *
 * Marketing-safe by construction — NEVER put internal metrics, thresholds, or
 * anything non-public here.
 */

import { SITE_CONFIG } from '@/data/site-constants';

/**
 * The four headline stats shown on the About page.
 *
 * `labelKey` is NAMESPACE-RELATIVE to `public.about` (e.g. `'stats.students'`,
 * not the full `public.about.stats.students`): `about-content.tsx` calls
 * `useTranslations('public.about')`, so `t('stats.students')` is correct. The
 * server-side company builder reads raw `en.json` and prepends `public.about.`
 * itself. Do NOT store the full-path key — it regresses the About page.
 */
export const COMPANY_STATS: { value: number; suffix: string; labelKey: string }[] = [
  { value: 10000, suffix: '+', labelKey: 'stats.students' },
  { value: 19, suffix: '+', labelKey: 'stats.yearsTeaching' },
  { value: 50, suffix: '+', labelKey: 'stats.educators' },
  { value: 400000, suffix: '+', labelKey: 'stats.classesTaught' },
];

/**
 * Marketing-safe company identity facts. `name` / `tagline` / `url` re-exported
 * from `SITE_CONFIG` (single source); `legalName` is canonically defined HERE —
 * `SITE_CONFIG` has no `legalName` field (it only appears embedded in
 * `SITE_CONFIG.copyright`), so it cannot be re-exported.
 */
export const COMPANY_FACTS = {
  name: SITE_CONFIG.name,
  legalName: 'EiFO Academy LLC',
  tagline: SITE_CONFIG.tagline,
  url: SITE_CONFIG.url,
} as const;
